import { useEffect, useMemo, useState } from 'react';
import { ImageOff } from 'lucide-react';

type ImageWithFallbackProps = {
  src?: string;
  alt?: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  /** Placeholder image URL (e.g. /placeholder.svg). When provided, used while loading and on error. When omitted, the icon fallback (ImageOff in muted box) is used instead. */
  placeholderSrc?: string;
  /**
   * Base URL for the image proxy. If provided, the component will retry failed
   * external images via: `${proxyBaseUrl}${encodeURIComponent(originalUrl)}`
   */
  proxyBaseUrl?: string;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  /** Custom icon when showing the icon fallback (no image / error). Defaults to ImageOff. */
  fallbackIcon?: React.ReactNode;
  /** Optional class for the icon fallback container (e.g. for size). */
  fallbackClassName?: string;
  /**
   * When true, wraps the image/fallback in a container with the same border and hover ring
   * as deerlake product thumbnails: rounded-md, border, bg-muted, hover:ring-2 hover:ring-ring.
   */
  thumbnail?: boolean;
};

const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({
  src,
  alt = '',
  width,
  height,
  className,
  placeholderSrc,
  proxyBaseUrl,
  objectFit = 'contain',
  fallbackIcon,
  fallbackClassName,
  thumbnail = false,
}) => {
  const [imageSrc, setImageSrc] = useState(src || placeholderSrc || '');
  const [isLoading, setIsLoading] = useState(!!src);
  const [didTryProxy, setDidTryProxy] = useState(false);
  /** When true, show the icon fallback (ImageOff) instead of any image. */
  const [useIconFallback, setUseIconFallback] = useState(!src && !placeholderSrc);

  const isExternalUrl = useMemo(() => {
    if (!src) return false;
    return /^https?:\/\//i.test(src);
  }, [src]);

  const proxyUrl = useMemo(() => {
    if (!src || !proxyBaseUrl) return '';
    return `${proxyBaseUrl}${encodeURIComponent(src)}`;
  }, [proxyBaseUrl, src]);

  useEffect(() => {
    setDidTryProxy(false);
    setUseIconFallback(!src && !placeholderSrc);
    if (src) {
      setIsLoading(true);
      setImageSrc(src);
    } else if (placeholderSrc) {
      setIsLoading(false);
      setImageSrc(placeholderSrc);
    } else {
      setImageSrc('');
    }
  }, [src, placeholderSrc]);

  const handleError = () => {
    if (src && isExternalUrl && !didTryProxy && proxyBaseUrl) {
      setDidTryProxy(true);
      setIsLoading(true);
      setImageSrc(proxyUrl);
      return;
    }

    if (placeholderSrc && imageSrc !== placeholderSrc) {
      setIsLoading(false);
      setImageSrc(placeholderSrc);
      return;
    }

    setUseIconFallback(true);
    setIsLoading(false);
    setImageSrc('');
  };

  const containerStyle =
    width && height
      ? {
          width: typeof width === 'number' ? `${width}px` : width,
          height: typeof height === 'number' ? `${height}px` : height,
        }
      : undefined;

  const thumbnailWrapperClass =
    'flex-shrink-0 rounded-md overflow-hidden border bg-muted hover:ring-2 hover:ring-ring transition-all';

  const content = useIconFallback ? (
    <div
      style={containerStyle}
      className={['flex items-center justify-center bg-muted', fallbackClassName, className]
        .filter(Boolean)
        .join(' ')}
    >
      {fallbackIcon ?? <ImageOff className='h-4 w-4 text-muted-foreground/50' />}
    </div>
  ) : (
    <div style={containerStyle} className={className}>
      {isLoading && placeholderSrc && (
        <img
          src={placeholderSrc}
          alt='Loading...'
          style={{ width: '100%', height: '100%', objectFit: objectFit }}
        />
      )}
      <img
        src={imageSrc}
        alt={alt}
        style={{
          width: '100%',
          height: '100%',
          objectFit: objectFit,
          display: isLoading ? 'none' : 'block',
        }}
        referrerPolicy='no-referrer'
        onLoad={() => setIsLoading(false)}
        onError={handleError}
      />
    </div>
  );

  if (thumbnail) {
    return (
      <div className={thumbnailWrapperClass} style={containerStyle}>
        {content}
      </div>
    );
  }

  return content;
};

export default ImageWithFallback;
